import { useCallback } from "react";
import { actionHandler } from "@/services";
import { useAppStore } from "@/stores";
import {
  OptInTournament,
  OptOutTournament,
  type CampaignSchema,
} from "@/types";
import { campaignKey } from "@/campaigns/campaign-key";
import { useCampaignNavigation } from "@/contexts/campaign-navigation";
import { recordOptInPromptJoined } from "@/utils/opt-in-prompt-memory";

export type OptInCallbacks = {
  /** The server confirmed the opt-in. */
  onSuccess?: () => void;
  /** It did not happen; the optimistic join has already been rolled back. */
  onFailure?: () => void;
};

export const useAction = () => {
  const setJoined = useAppStore((s) => s.setJoined);
  const navigateToCampaign = useCampaignNavigation();

  const optIn = useCallback(
    (campaign: CampaignSchema, callbacks?: OptInCallbacks) => {
      // Opt-in is type-agnostic: the join state every campaign type reads lives
      // in `joinedById` (keyed by campaignKey), fed by the `campaign_progress`
      // stream. Write it optimistically; the stream reconciles on its next push,
      // and a failed action rolls it back (see action.handler).
      const key = campaignKey(campaign);
      setJoined(key, true);
      actionHandler.sendAction(OptInTournament, campaign.id, {
        key,
        onOptInFailure: callbacks?.onFailure,
        onOptInSuccess: () => {
          // Any opt-in that LANDED — a home card, the floating pill, the prompt itself — is the
          // player's answer, so the opt-in prompt never asks about this campaign afterwards. Not
          // written with the optimistic join: a failed one must leave the prompt free to ask.
          const playerHash = useAppStore.getState().playerHash;
          if (playerHash) recordOptInPromptJoined(playerHash, key);
          callbacks?.onSuccess?.();
          navigateToCampaign(key);
        },
      });
    },
    [setJoined, navigateToCampaign]
  );

  const optOut = useCallback((campaignId: number) => {
    actionHandler.sendAction(OptOutTournament, campaignId);
  }, []);

  /** Restart a chase's run (targets and spins back to zero). The backend rules on it; the
   *  progress push carries the outcome. */
  const restartChase = useCallback((campaign: CampaignSchema) => {
    actionHandler.sendRestart(campaign.id);
  }, []);

  return { optIn, optOut, restartChase };
};
